import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a deadline date for display in the navigation
 * Returns either a countdown or the actual date/time depending on context
 */
export function formatDeadlineDisplay(
  deadline: string | Date,
  options: {
    showCountdown?: boolean
    isEditingPeriod?: boolean
    timeZone?: string
  } = {}
): string {
  const { 
    showCountdown = true, 
    isEditingPeriod = false, 
    timeZone = 'Europe/London' // Default to UK timezone for Premier League
  } = options

  const deadlineDate = new Date(deadline)
  const now = new Date()
  const timeUntil = deadlineDate.getTime() - now.getTime()

  // If deadline has passed
  if (timeUntil <= 0) {
    return 'Deadline passed'
  }

  // During editing period or when explicitly requested, show actual date/time
  if (isEditingPeriod || !showCountdown) {
    return deadlineDate.toLocaleString('en-GB', {
      timeZone,
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  }

  // Default countdown display
  const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24))
  const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60))
  
  if (days > 0) {
    return `${days}d ${hours}h`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else {
    return `${minutes}m`
  }
}

/**
 * Determine if the system is currently in an editing period
 * Business rule: After gameweek deadline passes until next gameweek deadline
 */
export function isInEditingPeriod(
  deadline: string | Date,
  nextGameweekStart?: string | Date
): boolean {
  const deadlineDate = new Date(deadline)
  const now = new Date()
  
  // Simple logic: we're in editing period if current deadline has passed
  // The navigation logic handles which gameweek/deadline to show
  return now > deadlineDate
}
